import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, LayoutGrid, List, CheckSquare, LogOut, Shield, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  const regularNavItems = [
    { path: "/board", label: "Board", icon: LayoutGrid, testId: "nav-board" },
    { path: "/list", label: "List", icon: List, testId: "nav-list" },
  ];

  const adminNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { path: "/admin", label: "Admin Panel", icon: Shield, testId: "nav-admin" },
    ...regularNavItems,
  ];

  const navItems = user?.is_admin ? adminNavItems : regularNavItems;

  return (
    <aside
      className="w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col"
      data-testid="sidebar"
    >
      <div className="p-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2" data-testid="app-logo">
          <CheckSquare className="w-8 h-8 text-blue-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TaskFlow</h1>
        </div>
      </div>

      <nav className="flex-1 p-4" data-testid="sidebar-nav">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={item.testId}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-50 dark:bg-emerald-500/10 text-blue-700 dark:text-emerald-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Theme Toggle */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-800">
        <Button
          onClick={toggleTheme}
          variant="outline"
          className="w-full dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          data-testid="theme-toggle-button"
        >
          {isDarkMode ? (
            <>
              <Sun className="w-4 h-4 mr-2" />
              Light Mode
            </>
          ) : (
            <>
              <Moon className="w-4 h-4 mr-2" />
              Dark Mode
            </>
          )}
        </Button>
      </div>

      {/* User Profile & Logout */}
      {user && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-testid="user-profile">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="dark:bg-gray-700 dark:text-gray-200">{user.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-testid="user-name">
                  {user.name}
                </p>
                {user.is_admin && (
                  <Shield className="w-3 h-3 text-purple-600 dark:text-emerald-400" data-testid="admin-badge" />
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-testid="user-email">
                {user.email}
              </p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="w-full dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      )}

      <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-testid="sidebar-footer">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Built with Notion-style simplicity
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
